/**
 * RitualEarningsSection Component
 * Displays earnings data for paid and donation-based rituals.
 * Only visible to ritual owners and organizers.
 *
 * Paid rituals:     Shows total earnings + "X participants @ $Y"
 * Donation-based:   Shows total earnings only (no fixed price)
 */

import { ArrowRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "@/language"
import type { RitualDetailData } from "../../types/detail.types"

interface RitualEarningsSectionProps {
  data: RitualDetailData
}

export function RitualEarningsSection({ data }: RitualEarningsSectionProps) {
  const { t } = useTranslation("rituals")
  const navigate = useNavigate()

  // Only show for owners/organizers of paid or donation-based rituals
  if (!data.isOwner && !data.isOrganizer) return null
  if (!data.isPaid && !data.isDonationBased) return null
  if (!data.ritualEarnings) return null

  const { totalEarnings, paidParticipantCount, perParticipantAmount } = data.ritualEarnings

  const handleNavigateToEarnings = () => {
    navigate("/settings?section=payments-earnings")
  }

  return (
    <div className="rounded-[20px] p-5 space-y-3 border border-[#FFFFFF1A] bg-[#FFFFFF1A]">
      {/* Header row: title + link */}
      <div className="flex items-center justify-between">
        <h3 className="text-[18px] leading-7.5 font-semibold text-primary uppercase">
          {t("ritualSidebar.earnings")}
        </h3>
        <button
          type="button"
          onClick={handleNavigateToEarnings}
          className="flex items-center gap-1 text-sm text-text-primary cursor-pointer"
        >
          <span>{t("ritualSidebar.paymentsAndEarnings")}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Divider */}
      <div className="h-px bg-[#F2F0EB33]" />

      {/* Earnings amount + description */}
      <div>
        <div className="flex justify-between items-center">
          <p className="text-[16px] font-bold leading-[34px] text-text-primary">
            $
            {totalEarnings.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          {/* Participant breakdown — Paid rituals only */}
          {data.isPaid &&
            paidParticipantCount !== undefined &&
            perParticipantAmount !== undefined && (
              <p className="text-sm text-[#949652]">
                {paidParticipantCount} {t("ritualSidebar.participantsAt")} ${perParticipantAmount}
              </p>
            )}
        </div>
        <p className="text-[16px] text-[#F2F0EB80] mt-0.5">{t("ritualSidebar.totalEarnings")}</p>
      </div>
    </div>
  )
}
